import { Badge, Box, Button, Divider, Flex, HStack, Text, VStack } from '@chakra-ui/react'
import { useProfile } from '@contexts/user/ProfileContext'
import {
  Encounter,
  encounterTypeName,
  FieldMapValue,
  getFullName,
  PlanItem,
  PlanItemCategory,
} from '@hb/shared'
import { SolidActionButton } from '@components/Buttons'
import { formatEncounterDate } from '../utils'

interface BloodPressure {
  systolic: number
  diastolic: number
}

const planCategoryLabel: Record<PlanItemCategory, string> = {
  labs: 'Labs',
  referral: 'Referral',
  education: 'Education',
  'return-visit': 'Return Visit',
  rx: 'Rx',
  procedure: 'Procedure',
}

const planCategoryColor: Record<PlanItemCategory, string> = {
  labs: 'purple',
  referral: 'blue',
  education: 'teal',
  'return-visit': 'green',
  rx: 'orange',
  procedure: 'red',
}

const BpText = ({ bp }: { bp: BloodPressure }) => (
  <Text fontSize="sm" fontWeight={600}>
    {bp.systolic}/{bp.diastolic} mmHg
  </Text>
)

const PLAN_PREVIEW_LIMIT = 4
const ASSESSMENT_PREVIEW_CHARS = 400

export const EncounterQuickAuthView = ({
  encounter,
  onEdit,
}: {
  encounter: Encounter
  onEdit: () => void
}) => {
  // §1.6 Quick Authenticate: summary must include the patient name (display only).
  const { user } = useProfile()
  const patientName = user ? getFullName(user) : null
  const data = (encounter.data ?? {}) as FieldMapValue
  const vital = data?.vital as Record<string, any> | undefined
  const bp = vital?.bloodPressure as BloodPressure | undefined
  const weight = vital?.weight as number | undefined

  const assessment = data?.assessment as string | undefined
  const planItems = data?.planItems as PlanItem[] | undefined
  const assessmentPreview =
    assessment && assessment.length > ASSESSMENT_PREVIEW_CHARS
      ? assessment.slice(0, ASSESSMENT_PREVIEW_CHARS) + '…'
      : assessment

  const planPreview = planItems?.slice(0, PLAN_PREVIEW_LIMIT) ?? []
  const remainingPlan = (planItems?.length ?? 0) - planPreview.length

  return (
    <VStack align="stretch" spacing={0} w="100%" h="100%">
      <Box flex={1} overflowY="auto" px={4} py={4}>
        <VStack align="stretch" spacing={4}>
          {/* Patient */}
          {patientName && (
            <Box>
              <Text
                fontSize="xs"
                fontWeight={600}
                color="gray.500"
                textTransform="uppercase"
                mb={0.5}>
                Patient
              </Text>
              <Text fontSize="sm" fontWeight={600}>
                {patientName}
              </Text>
            </Box>
          )}

          {/* Visit info */}
          <Box>
            <Text
              fontSize="xs"
              fontWeight={600}
              color="gray.500"
              textTransform="uppercase"
              mb={0.5}>
              Visit
            </Text>
            <Text fontSize="sm" fontWeight={600}>
              {encounterTypeName[encounter.type]}
            </Text>
            <Text fontSize="sm" color="gray.500">
              {encounter.date ? formatEncounterDate(encounter.date) : '—'}
            </Text>
          </Box>

          {/* Key vitals */}
          {(bp || weight != null) && (
            <>
              <Divider />
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight={600}
                  color="gray.500"
                  textTransform="uppercase"
                  mb={1}>
                  Vitals
                </Text>
                <HStack spacing={4} wrap="wrap">
                  {bp && (
                    <Flex align="center" gap={2}>
                      <Text fontSize="xs" color="gray.500">
                        BP
                      </Text>
                      <BpText bp={bp} />
                    </Flex>
                  )}
                  {weight != null && (
                    <Flex align="center" gap={2}>
                      <Text fontSize="xs" color="gray.500">
                        Weight
                      </Text>
                      <Text fontSize="sm" fontWeight={600}>
                        {weight} lbs
                      </Text>
                    </Flex>
                  )}
                </HStack>
              </Box>
            </>
          )}

          {/* Assessment */}
          {assessmentPreview && (
            <>
              <Divider />
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight={600}
                  color="gray.500"
                  textTransform="uppercase"
                  mb={1}>
                  Assessment
                </Text>
                <Text
                  fontSize="sm"
                  whiteSpace="pre-wrap"
                  color="gray.800"
                  _dark={{ color: 'gray.200' }}>
                  {assessmentPreview}
                </Text>
              </Box>
            </>
          )}

          {/* Plan */}
          {planPreview.length > 0 && (
            <>
              <Divider />
              <Box>
                <Text
                  fontSize="xs"
                  fontWeight={600}
                  color="gray.500"
                  textTransform="uppercase"
                  mb={1}>
                  Plan
                </Text>
                <VStack align="stretch" spacing={1.5}>
                  {planPreview.map((item, i) => (
                    <Flex key={i} align="flex-start" gap={2}>
                      <Badge
                        colorScheme={planCategoryColor[item.category] ?? 'gray'}
                        fontSize="2xs"
                        px={1.5}
                        py={0.5}
                        flexShrink={0}
                        mt={0.5}>
                        {planCategoryLabel[item.category] ?? item.category}
                      </Badge>
                      <Text fontSize="sm">{item.text}</Text>
                    </Flex>
                  ))}
                  {remainingPlan > 0 && (
                    <Text fontSize="xs" color="gray.500" fontStyle="italic">
                      +{remainingPlan} more — open full note to view
                    </Text>
                  )}
                </VStack>
              </Box>
            </>
          )}

          {!assessmentPreview && planPreview.length === 0 && (
            <>
              <Divider />
              <Text fontSize="sm" color="gray.500" fontStyle="italic">
                No assessment or plan recorded.
              </Text>
            </>
          )}
        </VStack>
      </Box>

      {/* Authenticate footer */}
      <Box
        borderTop="2px solid"
        borderColor="green.200"
        bg="green.50"
        _dark={{ bg: 'green.900', borderColor: 'green.700' }}
        px={4}
        py={3}>
        <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }} mb={2}>
          Review the note, then open it to complete the Time &amp; Work Log and authenticate.
        </Text>
        <HStack spacing={3}>
          <SolidActionButton size="sm" onClick={onEdit}>
            Open to Authenticate
          </SolidActionButton>
          <Button size="sm" variant="outline" colorScheme="gray" onClick={onEdit}>
            Open Full Note
          </Button>
        </HStack>
      </Box>
    </VStack>
  )
}
